
import React, { useState, useEffect } from 'react';
import { ConversationsList } from './messaging/ConversationsList';
import { MessageThread } from './messaging/MessageThread';
import { type Conversation } from '@/hooks/useConversations';
import { useConversations, useDeleteEmptyConversation } from '@/hooks/useConversations';
import { useSearchParams } from 'react-router-dom';

interface ChatInterfaceProps {
  onBack: () => void;
}

export const ChatInterface = ({ onBack }: ChatInterfaceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const { data: conversations = [] } = useConversations();
  const deleteEmptyConversation = useDeleteEmptyConversation();
  
  // Auto-select conversation from URL parameter
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation) {
        setSelectedConversation(conversation);
        // Clear the URL parameter after selecting
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('conversation');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [conversations, searchParams, setSearchParams]);

  // Clean up empty conversations when navigating away from a conversation
  const handleBackFromConversation = () => {
    if (selectedConversation) {
      // Try to delete the conversation if it's empty
      deleteEmptyConversation.mutate(selectedConversation.id);
    }
    setSelectedConversation(null);
  };

  // Clean up when navigating away from chat interface entirely
  useEffect(() => {
    return () => {
      if (selectedConversation) {
        deleteEmptyConversation.mutate(selectedConversation.id);
      }
    };
  }, [selectedConversation]); // Removed deleteEmptyConversation from dependencies

  if (selectedConversation) {
    return (
      <MessageThread
        conversation={selectedConversation}
        onBack={handleBackFromConversation}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ConversationsList
        onSelectConversation={setSelectedConversation}
        selectedConversationId={selectedConversation?.id}
      />
    </div>
  );
};
